import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SelectedPracticesPanel = ({ 
  selectedPractices, 
  onRemovePractice, 
  onClearAll, 
  onProceedToQuestionnaire 
}) => {
  const totalComplexity = selectedPractices?.reduce((acc, practice) => {
    const complexityScore = {
      'low': 1,
      'medium': 2,
      'high': 3,
      'expert': 4
    };
    return acc + (complexityScore?.[practice?.complexity] || 0);
  }, 0);

  const getOverallComplexity = () => {
    if (selectedPractices?.length === 0) return 'None';
    const avgComplexity = totalComplexity / selectedPractices?.length;
    if (avgComplexity <= 1.5) return 'Low';
    if (avgComplexity <= 2.5) return 'Medium';
    if (avgComplexity <= 3.5) return 'High';
    return 'Expert';
  };

  const estimatedTimeline = selectedPractices?.reduce((total, practice) => {
    const timelineWeeks = parseInt(practice?.timeline?.split('-')?.[0]) || 0;
    return total + timelineWeeks;
  }, 0);

  // Check for potential conflicts or dependencies
  const checkDependencies = () => {
    const warnings = [];
    const codes = selectedPractices?.map(p => p?.code);
    
    // Example dependency checks
    if (codes?.includes('1QM') && !codes?.includes('1D2')) {
      warnings?.push('Quality Management (1QM) typically requires Master Data (1D2)');
    }
    if (codes?.includes('5D2') && codes?.includes('5D3')) {
      warnings?.push('Scope overlap detected between 5D2 and 5D3 practices');
    }
    
    return warnings;
  };

  const warnings = checkDependencies();

  if (selectedPractices?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Practices Selected</h3>
          <p className="text-muted-foreground">
            Select SAP Best Practices from the catalog to build your engagement scope.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Selected Practices</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {selectedPractices?.length} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            iconName="X"
          >
            Clear All
          </Button>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
        <div>
          <div className="text-sm text-muted-foreground">Overall Complexity</div>
          <div className="text-lg font-semibold text-foreground">{getOverallComplexity()}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Est. Timeline</div>
          <div className="text-lg font-semibold text-foreground">{estimatedTimeline}+ weeks</div>
        </div>
      </div>
      {/* Warnings */}
      {warnings?.length > 0 && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-warning mb-1">Scope Considerations</h4>
              <ul className="space-y-1">
                {warnings?.map((warning, index) => (
                  <li key={index} className="text-sm text-warning/80">
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Selected Practices List */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {selectedPractices?.map((practice) => (
          <div 
            key={practice?.id}
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-mono">
                  {practice?.code}
                </span>
                <span className="text-sm font-medium text-foreground truncate">
                  {practice?.title}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {practice?.timeline} • {practice?.complexity} complexity
              </div>
            </div>
            <button
              onClick={() => onRemovePractice(practice?.id)}
              className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 nav-transition"
              title="Remove practice"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        ))}
      </div>
      {/* Actions */}
      <div className="pt-4 border-t border-border">
        <Button
          variant="default"
          fullWidth
          onClick={onProceedToQuestionnaire}
          iconName="ArrowRight"
          iconPosition="right"
          disabled={selectedPractices?.length === 0}
        >
          Proceed to Questionnaire ({selectedPractices?.length} practices)
        </Button>
      </div>
    </div>
  );
};

export default SelectedPracticesPanel;